import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import * as Api from "./api";
import {
  PartnerUserId as ID,
  IPartnerUser as Entity,
  IPartnerUserSaveReq as SaveReq,
  IPartnerUserUpdateReq as UpdateReq,
  IPartnerUserChangePasswordReq as ChangePasswordReq
} from "./types";
import { constants, paths } from "./utils";

interface ConfirmDialogStateTypes {
  open: boolean;
  onConfirm?: () => void;
}

interface EditStateTypes {
  fetching: boolean;
  data?: Entity;
}

export const useCreate = () => {
  const router = useRouter();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [saving, setSaving] = useState(false);

  const [confirmDialogState, setConfirmDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDialog = useCallback(() => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  }, []);
  const handleSave = useCallback(
    (req: SaveReq) => {
      setConfirmDialogState({
        open: true,
        onConfirm: async () => {
          try {
            setSaving(true);
            await Api.save(req);
            await router.push(paths.index);
            setMessage(
              "success",
              `สร้าง ${constants.label} "${req.username}" สำเร็จ`
            );
          } catch (e) {
            setSaving(false);
            handleError(e);
            handleCloseDialog();
          }
        }
      });
    },
    [setMessage, handleError, router, handleCloseDialog]
  );

  return { saving, confirmDialogState, handleSave, handleCloseDialog };
};

export const useUpdate = (id: ID) => {
  const router = useRouter();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [saving, setSaving] = useState(false);
  const [confirmDialogState, setConfirmDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDialog = useCallback(() => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  }, []);
  const handleSave = useCallback(
    (req: UpdateReq) => {
      setConfirmDialogState({
        open: true,
        onConfirm: async () => {
          try {
            setSaving(true);
            await Api.update(id, req);
            await router.push(paths.index);
            setMessage(
              "success",
              `แก้ไข ${constants.label} "${req.username}" สำเร็จ`
            );
          } catch (e) {
            setSaving(false);
            handleError(e);
            handleCloseDialog();
          }
        }
      });
    },
    [id, setMessage, handleError, router, handleCloseDialog]
  );

  const [state, setState] = useState<EditStateTypes>({ fetching: true });

  const fetchEntity = useCallback(async () => {
    setState(d => ({ ...d, fetching: true }));
    try {
      const resp = await Api.get(id);
      setState({ fetching: false, data: resp.value });
    } catch (e) {
      handleError(e);
      setState({ fetching: false, data: undefined });
    }
  }, [id, handleError]);

  useEffect(() => {
    if (id) {
      fetchEntity();
    } else {
      setState({ fetching: false, data: undefined });
    }
  }, [id, fetchEntity]);

  const { fetching, data } = state;

  return {
    saving,
    fetching,
    data,
    confirmDialogState,
    handleSave,
    handleCloseDialog
  };
};

export const useChangePassword = (id: ID) => {
  const router = useRouter();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<EditStateTypes>({ fetching: true });
  const { fetching, data } = state;

  const [saving, setSaving] = useState(false);
  const [confirmDialogState, setConfirmDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDialog = useCallback(() => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  }, []);
  const handleSave = useCallback(
    (req: ChangePasswordReq) => {
      setConfirmDialogState({
        open: true,
        onConfirm: async () => {
          try {
            setSaving(true);
            await Api.changePassword(id, req);
            await router.push(paths.index);
            setMessage(
              "success",
              `เปลี่ยน Password ${constants.label} "${data?.fullName}" สำเร็จ`
            );
          } catch (e) {
            setSaving(false);
            handleError(e);
            handleCloseDialog();
          }
        }
      });
    },
    [id, data?.fullName, setMessage, handleError, router, handleCloseDialog]
  );

  const fetchEntity = useCallback(async () => {
    setState(d => ({ ...d, fetching: true }));
    try {
      const resp = await Api.get(id);
      setState({ fetching: false, data: resp.value });
    } catch (e) {
      handleError(e);
      setState({ fetching: false, data: undefined });
    }
  }, [id, handleError]);

  useEffect(() => {
    if (id) {
      fetchEntity();
    } else {
      setState({ fetching: false, data: undefined });
    }
  }, [id, fetchEntity]);

  return {
    fetching,
    saving,
    confirmDialogState,
    handleSave,
    handleCloseDialog
  };
};
